import React, { useState } from "react";

import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { PersonnelGroup, Side } from "../../../types/DepthChartTypes";
import { useSchemes } from "../../../contexts/SchemesContext";
import { KickOffReturnPlay, PuntReturnPlay } from "../../../types/SchemesTypes";
import { useClub } from "../../../contexts/ClubContext";

export const PuntReturnSchemes = () => {
  const {
    puntReturnPersonnel,
    puntReturnPlays,
    savePersonnelGroup,
    deletePersonnelGroup,
    savePuntReturnPlay,
    deletePuntReturnPlay,
  } = useSchemes();
  const { club } = useClub();
  // Dialog state
  const [playDialogOpen, setPlayDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"play" | "group" | null>(null);

  // Form state
  const [currentPersonnelGroup, setCurrentPersonnelGroup] =
    useState<PersonnelGroup>({
      name: "",
      description: "",
      side: "PUNT_RETURN" as Side,
    });
  const [currentPlay, setCurrentPlay] = useState<PuntReturnPlay>({
    name: "",
    description: "",
    club_id: club.id,
  });

  // Snackbar
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Play handlers
  const handleEditPlay = (playId: number | undefined) => {
    if (playId !== undefined) {
      setCurrentPlay(puntReturnPlays.find((play) => play.id === playId) || currentPlay);
      setPlayDialogOpen(true);
    }
  };

  const handleSavePlay = async () => {
    if (!currentPlay.name) {
      setAlert({ open: true, message: "Name is required", severity: "error" });
      return;
    }
    await savePuntReturnPlay(currentPlay);
    setPlayDialogOpen(false);
    setAlert({ open: true, message: "Play saved", severity: "success" });
  };

  const handleDeletePlay = (playId: number | undefined) => {
    if (playId !== undefined) {
      setDeleteType("play");
      setCurrentPlay(puntReturnPlays.find((play) => play.id === playId) || currentPlay);
      setDeleteDialogOpen(true);
    }
  };

  // PersonnelGroup handlers
  const handleAddGroup = () => {
    setCurrentPersonnelGroup({
      name: "",
      description: "",
      side: "PUNT_RETURN" as Side,
    });
    setGroupDialogOpen(true);
  };
  const handleEditGroup = (personnelGroupId: number | undefined) => {
    if (personnelGroupId !== undefined) {
      setCurrentPersonnelGroup(
        puntReturnPersonnel.find((group) => group.id === personnelGroupId) ||
          currentPersonnelGroup
      );
      setGroupDialogOpen(true);
    }
  };
  const handleSaveGroup = async () => {
    if (!currentPersonnelGroup.name) {
      setAlert({ open: true, message: "Name is required", severity: "error" });
      return;
    }
    await savePersonnelGroup({
      id: currentPersonnelGroup.id,
      name: currentPersonnelGroup.name,
      description: currentPersonnelGroup.description || "",
      side: "PUNT_RETURN" as Side,
    });
    setGroupDialogOpen(false);
    setCurrentPersonnelGroup({
      name: "",
      description: "",
      side: "PUNT_RETURN" as Side,
    })
    setAlert({
      open: true,
      message: "PersonnelGroup saved",
      severity: "success",
    });
  };
  const handleDeleteGroup = (personnelGroupId: number | undefined) => {
    if (personnelGroupId !== undefined) {
      setDeleteType("group");
      setCurrentPersonnelGroup(
        puntReturnPersonnel.find((group) => group.id === personnelGroupId) ||
          currentPersonnelGroup
      );
      setDeleteDialogOpen(true);
    }
  };
  const handleConfirmDelete = () => {
    if (deleteType === "play" && currentPlay.id !== undefined) {
      deletePuntReturnPlay(currentPlay.id);
      setAlert({ open: true, message: "Play deleted", severity: "success" });
    } else if (
      deleteType === "group" &&
      currentPersonnelGroup.id !== undefined
    ) {
      deletePersonnelGroup(currentPersonnelGroup.id);
      setCurrentPersonnelGroup({
        name: "",
        description: "",
        side: "PUNT_RETURN" as Side,
      });
      setAlert({ open: true, message: "Group deleted", severity: "success" });
    }
    setDeleteDialogOpen(false);
    setDeleteType(null);
  };
  const handleCloseAlert = () => setAlert({ ...alert, open: false });

  return (
    <Box
      sx={{
        p: 1,
        maxWidth: 1600,
        width: "100%",
        bgcolor: "#121212",
        color: "white",
        height: "calc(100vh - 600px)",
        boxSizing: "border-box",
      }}
    >
      {/* Main content area */}
      <Box
        sx={{
          bgcolor: "#1e1e1e",
          p: 2,
          borderRadius: 1,
          height: "auto",
          minHeight: "calc(100vh - 500px)",
          overflow: "visible",
          display: "flex",
          gap: 2,
          flexDirection: "row",
        }}
      >
        {/* Field Goal Personnel */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
              Punt Return Personnel
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add
            </Button>
          </Box>
          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {puntReturnPersonnel.map((item, idx) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleEditGroup(item.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteGroup(item.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{item.name}</Typography>}
                  secondary={
                    item.description && (
                      <Typography color="rgba(255,255,255,0.7)" variant="body2">
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            {puntReturnPersonnel.length === 0 && (
              <ListItem
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No Personnel Groups Found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>

        {/* Punt Return Plays */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#232323",
            borderRadius: 1,
            p: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
              Punt Return Plays
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPlayDialogOpen(true)}
              sx={{
                color: "white",
                bgcolor: "rgba(123, 45, 58, 0.9)",
                "&:hover": { bgcolor: "rgba(123, 45, 58, 0.7)" },
                fontWeight: 600,
              }}
            >
              Add
            </Button>
          </Box>
          <List sx={{ width: "100%", bgcolor: "transparent" }}>
            {puntReturnPlays.map((item, idx) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleEditPlay(item.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeletePlay(item.id)}
                      sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={<Typography color="white">{item.name}</Typography>}
                  secondary={
                    item.description && (
                      <Typography color="rgba(255,255,255,0.7)" variant="body2">
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
            {puntReturnPlays.length === 0 && (
              <ListItem
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
              >
                <ListItemText
                  primary={
                    <Typography color="rgba(255, 255, 255, 0.7)">
                      No Plays Found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Box>

      {/* Play Dialog */}
      <Dialog open={playDialogOpen} onClose={() => setPlayDialogOpen(false)}>
        <DialogTitle>
          {currentPlay.id !== undefined ? "Edit Play" : "Add Play"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* name */}
            <TextField
              fullWidth
              label="Name"
              value={currentPlay.name}
              onChange={(e) =>
                setCurrentPlay({ ...currentPlay, name: e.target.value })
              }
              margin="normal"
            />
            {/* personnel group */}
            <FormControl fullWidth>
              <InputLabel>Personnel Group</InputLabel>
              <Select
                value={currentPlay?.personnel_group?.id || ""}
                onChange={(e) =>
                  setCurrentPlay({
                    ...currentPlay,
                    personnel_group:
                      puntReturnPersonnel.find(
                        (group) => group.id === e.target.value
                      ) || currentPersonnelGroup,
                  })
                }
              >
                {puntReturnPersonnel.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* description */}
            <TextField
              fullWidth
              label="Description"
              value={currentPlay.description || ""}
              onChange={(e) =>
                setCurrentPlay({ ...currentPlay, description: e.target.value })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePlay} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)}>
        <DialogTitle>
          {currentPersonnelGroup.id !== undefined
            ? "Edit Personnel Group"
            : "Add Personnel Group"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={currentPersonnelGroup.name}
              onChange={(e) =>
                setCurrentPersonnelGroup({
                  ...currentPersonnelGroup,
                  name: e.target.value,
                })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={currentPersonnelGroup.description || ""}
              onChange={(e) =>
                setCurrentPersonnelGroup({
                  ...currentPersonnelGroup,
                  description: e.target.value,
                })
              }
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this{" "}
            {deleteType === "play" ? "play" : "personnel group"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
